import { useEffect, useRef } from 'react';
import { MessageSquare, Users } from 'lucide-react';

import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { QueryError } from '@/components/ui/QueryError';
import { Skeleton } from '@/components/ui/Skeleton';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { ApiRequestError } from '@/lib/apiClient';
import { cn } from '@/lib/utils';
import { parentApi } from '../api/parent.api';
import { MessageCard } from '../components/messages/MessageCard';
import { useParentChild, useSelectedChild } from '../store/child.store';
import { PARENT_PALETTE } from '../theme';

/** A screenful of messages. The server caps a page at fifty. */
const PAGE_SIZE = 10;

/** The page being read, as it sits in the address bar. */
type Filters = {
  page: number;
};

const DEFAULT_FILTERS: Filters = { page: 1 };

/**
 * Messages, for the parent of the child on screen.
 *
 * Reads `GET /parent/children/:uuid/messages` — the SMS and WhatsApp messages
 * the school office sent this family, from the same delivery log the office's
 * notification history is drawn from. Paged on the server.
 *
 * Read-only, and it says so. The product has no conversation table, so there is
 * no reply box: a compose field that went nowhere would be worse than none.
 *
 * The child is never in the address. It comes from the switcher in the sidebar,
 * and the server resolves which children the caller may see from their own
 * token — an id in the URL is an id somebody can change (CLAUDE.md §10).
 */
export function ParentMessagesPage() {
  const child = useSelectedChild();
  const isLoadingChildren = useParentChild((state) => state.isLoading);
  const childrenError = useParentChild((state) => state.error);
  const reloadChildren = useParentChild((state) => state.load);

  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS);
  const { page } = filters;

  // A new child starts at their own first page, not wherever the last one was.
  // The first child to arrive is not a switch, so a page restored from the
  // address stays.
  const shownChild = useRef(child?.uuid);
  useEffect(() => {
    const previous = shownChild.current;
    shownChild.current = child?.uuid;
    if (previous === undefined || previous === child?.uuid) return;
    setFilters({ page: 1 });
  }, [child?.uuid, setFilters]);

  const { data, isLoading, error, refetch } = useApiQuery(
    () => (child ? parentApi.messages(child.uuid, page, PAGE_SIZE) : Promise.resolve(null)),
    [child?.uuid, page],
  );

  // Switching children refetches; until the new page lands, the old one is
  // another child's family's messages and must not be drawn under this name.
  const inbox = data && data.child.uuid === child?.uuid ? data : null;

  if (isLoadingChildren) {
    return (
      <div className={cn(PARENT_PALETTE, 'flex flex-col gap-6')}>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 rounded-3xl" />
        <Skeleton className="h-40 rounded-3xl" />
      </div>
    );
  }

  if (childrenError) {
    return (
      <div className={cn(PARENT_PALETTE)}>
        <QueryError
          title="Could not load your children"
          error={new ApiRequestError(childrenError, 0, 'INTERNAL_ERROR')}
          onRetry={() => void reloadChildren()}
        />
      </div>
    );
  }

  if (!child) {
    return (
      <div className={cn(PARENT_PALETTE, 'card')}>
        <EmptyState
          icon={<Users className="h-5 w-5" />}
          title="No child linked to your account yet"
          description="Your school office links a parent's account to their child's record. Once that is done, the messages the school sends you appear here."
        />
      </div>
    );
  }

  return (
    <div className={cn(PARENT_PALETTE, 'flex flex-col gap-5')}>
      <header className="min-w-0">
        <h1 className="text-2xl font-bold tracking-tight text-ink">Notifications</h1>
        <p className="mt-1 text-sm text-ink-2">
          The SMS and WhatsApp messages your school has sent you about {child.fullName}. To reply,
          contact the school office.
        </p>
      </header>

      {error && (
        <QueryError title="Could not load your messages" error={error} onRetry={refetch} />
      )}

      {!error && !inbox && (
        <div className="flex flex-col gap-4" aria-busy="true">
          <span className="sr-only">Loading your messages</span>
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-40 rounded-3xl" />
          ))}
        </div>
      )}

      {!error && inbox && inbox.items.length === 0 && (
        <div className="card">
          <EmptyState
            icon={<MessageSquare className="h-5 w-5" />}
            title="No messages yet"
            description="When your school sends you an SMS or WhatsApp message about your child, a copy appears here so you can find it again."
          />
        </div>
      )}

      {!error && inbox && inbox.items.length > 0 && (
        <div className={cn('flex flex-col gap-4', isLoading && 'opacity-60 transition-opacity')}>
          {inbox.items.map((message) => (
            <MessageCard key={message.uuid} message={message} />
          ))}

          <Pagination
            meta={inbox.meta}
            onPageChange={(next) => setFilters({ page: next })}
            itemLabel="messages"
          />
        </div>
      )}
    </div>
  );
}
